import { Component, inject, input, signal, ViewChild, type InputSignal, type TemplateRef, type WritableSignal } from '@angular/core';
import { Card } from '../../../../shared/components/card/card/card';
import { CardHeader } from '../../../../shared/components/card/card-header/card-header';
import { CommonModule, formatCurrency } from '@angular/common';
import type { ChartConfiguration } from 'chart.js';
import dayjs from 'dayjs';
import { barChartDefaultOptions, lineChartDefaultOptions } from '../../../../interfaces/charts';
import { SummaryGgrDetailsDaily, SummaryGgrDetailsWeekly, SummaryGgrDetailsMonthly, DashboardType, SummaryGgrDetailsDailyItem, SummaryGgrDetailsTurnover, SummaryGgrDetailsTurnoverItemDataHourlyData, SummaryGgrDetailsOperations, TopCasinoGamesTop5, TopSportsMarketsTop5, FinancialLifeCycleItemCategory, FinancialLifeCycleItem, type FinancialChurn, type FinancialLifeCycle, type FinancialChurnDetailed, FinancialChurnType } from '../../../../interfaces/dashboard';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { UIChart } from 'primeng/chart';
import { EmptyChart } from '../../../../shared/components/empty-chart/empty-chart';
import { TableModule } from 'primeng/table';
import { DashboardService } from '../../../../services/dashboard.service';
import { SocketService } from '../../../../services/socket.service';
import { finalize } from 'rxjs';
import { Skeleton } from "primeng/skeleton";
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FinancialLifecycleCategoryPipe } from "../../../../shared/pipes/financial-lifecycle-category-pipe";
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FinancialChurnTypePipe } from "../../../../shared/pipes/financial-churn-type-pipe";
import { AutoCompleteModule, type AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { ReportsService } from '../../../../services/reports.service';
import { PaginatorModule, type PaginatorState } from 'primeng/paginator';
import { Order, type PageControl } from '../../../../interfaces/application';

@Component({
  selector: 'app-dashboard-operations',
  imports: [
    CommonModule,
    Card,
    CardHeader,
    ProgressSpinnerModule,
    UIChart,
    EmptyChart,
    TableModule,
    Skeleton,
    DialogModule,
    DatePickerModule,
    DividerModule,
    ReactiveFormsModule,
    FormsModule,
    FinancialLifecycleCategoryPipe,
    TooltipModule,
    SelectButtonModule,
    FinancialChurnTypePipe,
    AutoCompleteModule,
    PaginatorModule
],
  templateUrl: './dashboard-operations.html',
  styleUrl: './dashboard-operations.css',
})
export class DashboardOperations {
  @ViewChild('summaryGgrDetailsDailyChart') summaryGgrDetailsDailyChart?: UIChart;
  @ViewChild('summaryGgrDetailsWeeklyChart') summaryGgrDetailsWeeklyChart?: UIChart;
  @ViewChild('summaryGgrDetailsMonthlyChart') summaryGgrDetailsMonthlyChart?: UIChart;

  private readonly _dashboardService: DashboardService = inject(DashboardService);
  private readonly _reportsService: ReportsService = inject(ReportsService);
  private readonly _socketService: SocketService = inject(SocketService);

  public startDateFilter: InputSignal<Date|null> = input<Date|null>(null);
  public endDateFilter: InputSignal<Date|null> = input<Date|null>(null);

  public dialogVisible: boolean = false;
  public dialogTitle: WritableSignal<string> = signal<string>('');
  public selectedDialogChart: WritableSignal<TemplateRef<any>|null> = signal<TemplateRef<any>|null>(null);

  public summaryGgrDetailsOperationsLoading: WritableSignal<boolean> = signal<boolean>(false);
  public summaryGgrDetailsOperationsData: WritableSignal<SummaryGgrDetailsOperations|null> = signal<SummaryGgrDetailsOperations|null>(null);

  public summaryLifeCycleLoading: WritableSignal<boolean> = signal<boolean>(false);
  public summaryLifeCycleData: WritableSignal<FinancialLifeCycle|null> = signal<FinancialLifeCycle|null>(null);
  public financialLifeCycleItemCategory: typeof FinancialLifeCycleItemCategory = FinancialLifeCycleItemCategory;

  public financialBrandUtmSourceData: WritableSignal<any> = signal<any>(null);

  public churnDialogVisible: boolean = false;
  public summaryChurnType: typeof FinancialChurnType = FinancialChurnType;
  public summaryChurnLoading: WritableSignal<boolean> = signal<boolean>(false);
  public summaryChurnData: WritableSignal<FinancialChurn|null> = signal<FinancialChurn|null>(null);
  public summaryChurnShowType: FinancialChurnType = FinancialChurnType.BETS;
  public summaryChurnDetailedLoading: WritableSignal<boolean> = signal<boolean>(false);
  public summaryChurnDetailedData: WritableSignal<FinancialChurnDetailed|null> = signal<FinancialChurnDetailed|null>(null);
  public summaryChurnDetailedUtm: string = '';
  public summaryChurnUtmFilter: WritableSignal<string[]> = signal<string[]>([]);
  public summaryChurnPageControl: PageControl = {
    page: 1,
    take: 10,
    itemCount: 0,
    pageCount: 0,
    orderField: 'created_at',
    order: Order.DESC
  }

  public summaryGgrDetailsDailyLoading: WritableSignal<boolean> = signal<boolean>(false);
  public summaryGgrDetailsDailyData: WritableSignal<SummaryGgrDetailsDaily|null> = signal<SummaryGgrDetailsDaily|null>(null);
  public summaryGgrDetailsDailyChartData: WritableSignal<any> = signal<any>(null);
  public summaryGgrDetailsDailyOptions: WritableSignal<ChartConfiguration["options"]> = signal<ChartConfiguration["options"]>({...barChartDefaultOptions});

  public summaryGgrDetailsWeeklyLoading: WritableSignal<boolean> = signal<boolean>(false);
  public summaryGgrDetailsWeeklyData: WritableSignal<SummaryGgrDetailsWeekly|null> = signal<SummaryGgrDetailsWeekly|null>(null);
  public summaryGgrDetailsWeeklyChartData: WritableSignal<any> = signal<any>(null);
  public summaryGgrDetailsWeeklyOptions: WritableSignal<ChartConfiguration["options"]> = signal<ChartConfiguration["options"]>({...barChartDefaultOptions});

  public summaryGgrDetailsMonthlyLoading: WritableSignal<boolean> = signal<boolean>(false);
  public summaryGgrDetailsMonthlyData: WritableSignal<SummaryGgrDetailsMonthly|null> = signal<SummaryGgrDetailsMonthly|null>(null);
  public summaryGgrDetailsMonthlyChartData: WritableSignal<any> = signal<any>(null);
  public summaryGgrDetailsMonthlyOptions: WritableSignal<ChartConfiguration["options"]> = signal<ChartConfiguration["options"]>({...barChartDefaultOptions});

  public summaryTurnoverLoading: WritableSignal<boolean> = signal<boolean>(false);
  public summaryTurnoverData: WritableSignal<SummaryGgrDetailsTurnover|null> = signal<SummaryGgrDetailsTurnover|null>(null);
  public summaryTurnoverChartData: WritableSignal<any> = signal<any>(null);
  public summaryTurnoverOptions: WritableSignal<ChartConfiguration["options"]> = signal<ChartConfiguration["options"]>({...lineChartDefaultOptions});

  public topCasinoGamesTop5Loading: WritableSignal<boolean> = signal<boolean>(false);
  public topCasinoGamesTop5Data: WritableSignal<TopCasinoGamesTop5|null> = signal<TopCasinoGamesTop5|null>(null);

  public topSportsMarketsTop5Loading: WritableSignal<boolean> = signal<boolean>(false);
  public topSportsMarketsTop5Data: WritableSignal<TopSportsMarketsTop5|null> = signal<TopSportsMarketsTop5|null>(null);

  public ngOnInit(): void {
    this._initSocket();
  }

  private _initSocket(): void {
    this._socketService.echo
      .channel('dashboard')
      .listen('.updated', (data: { dashboard: DashboardType }) => console.log({data}));
  }

  private _initDashboard(): void {
    this.getBrandUtmSource();

    this.getSummaryGgrDetailsOperations();
    this.getSummaryLifeCycle();
    this.getSummaryChurn();
    this.getSummaryGgrDetailsDaily();
    this.getSummaryGgrDetailsWeekly();
    this.getSummaryGgrDetailsMonthly();
    this.getSummaryTurnover();

    this.topCasinoGamesTop5();
    this.topSportsMarketsTop5();
  }

  public ngOnChanges(): void {
    if(!this._isCompletedRangeDates()) {
      return;
    }

    this._initDashboard();
  }

  private _isCompletedRangeDates(): boolean {
    return this.startDateFilter() instanceof Date && this.endDateFilter() instanceof Date;
  }

  public openDialog(template: TemplateRef<any>, title: string): void {
    this.dialogTitle.set(title);
    this.selectedDialogChart.set(template);
    this.dialogVisible = true;
  }

  public closeDialog(): void {
    this.dialogVisible = false;
    this.dialogTitle.set('');
    this.selectedDialogChart.set(null);
  }

  public filterUtmMethodCompleteMethod(event: AutoCompleteCompleteEvent): void {
    this.summaryChurnUtmFilter.set(
      this.financialBrandUtmSourceData().utm_sources
        .filter((item: string) => item.toLowerCase().includes(event.query.toLowerCase()))
    )
  }

  public getBrandUtmSource(): void {
    this._reportsService.brandUtmSource()
      .subscribe({
        next: (response) => {
          this.financialBrandUtmSourceData.set(response.data);
        }
      })
  }

  public getSummaryGgrDetailsDaily(): void {
    this.summaryGgrDetailsDailyLoading.set(true);

    const filters = {
      start_date: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      end_date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    };

    if (!filters.start_date || !filters.end_date) {
      return;
    }

    this._dashboardService.summaryGgrDetailsDaily(filters)
      .pipe(finalize(() => this.summaryGgrDetailsDailyLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.summaryGgrDetailsDailyData.set(response.data);

            this.mountSummaryGgrDetailsDailyData();
            this.mountSummaryGgrDetailsDailyOptions();
          }
        }
      });
  }

  public mountSummaryGgrDetailsDailyData(): void {

    const colors = (length: number): string[] => {
      const keyIndexes = [length - 15, length - 8, length - 1];

      return new Array(length).fill(null).map((_, index) =>
        keyIndexes.includes(index) ? '#E03326' : '#6DA4FF'
      );
    }

    this.summaryGgrDetailsDailyData()?.last_30_days.reverse();

    this.summaryGgrDetailsDailyChartData.set({
      labels: this.summaryGgrDetailsDailyData()?.last_30_days
        .map((item: SummaryGgrDetailsDailyItem) => item.period)
        .concat(this.summaryGgrDetailsDailyData()?.current_day.period!) || [],
      datasets: [
        {
          label: '',
          data: this.summaryGgrDetailsDailyData()?.last_30_days
            .map((item: SummaryGgrDetailsDailyItem) => item.data.total_ggr)
            .concat(this.summaryGgrDetailsDailyData()?.current_day.data.total_ggr!) || [],
          borderWidth: 1,
          barPercentage: 0.7,
          categoryPercentage: 0.8,
          borderRadius: 4,
          backgroundColor: colors(this.summaryGgrDetailsDailyData()?.last_30_days.length! + 1),
          borderColor: colors(this.summaryGgrDetailsDailyData()?.last_30_days.length! + 1),
          customData: this.summaryGgrDetailsDailyData()?.last_30_days
            .map((item: SummaryGgrDetailsDailyItem) => item)
            .concat(this.summaryGgrDetailsDailyData()?.current_day!) || []
        }
      ]
    })

    this.summaryGgrDetailsDailyChart?.refresh();
  }

  public mountSummaryGgrDetailsDailyOptions(): void {
    this.summaryGgrDetailsDailyOptions.set({
      ...barChartDefaultOptions,
      maintainAspectRatio: false,
      plugins: {
        ...barChartDefaultOptions?.plugins,
        legend: {
          display: false
        },
        tooltip: {
          ...barChartDefaultOptions?.plugins?.tooltip,
          callbacks: {
            label: function(context: any) {
              return [
                context.dataset.customData[context.dataIndex].day_of_week,
                formatCurrency(Number(context.raw), 'pt_BR', 'R$')
              ];
            }
          }
        }
      },
      scales: {
        ...barChartDefaultOptions?.scales,
        y: {
          grid: {
            display: true,
          },
          ticks: {
            callback: function (value, index, ticks) {
              if (+value >= 1000000) {
                return `${(+value / 1000000)}M`;
              }

              if (+value > 1000) {
                return `${(+value / 1000)}K`;
              }

              return value
            }
          },
          beginAtZero: true
        }
      },
    });
  }

  public getSummaryGgrDetailsWeekly(): void {
    this.summaryGgrDetailsWeeklyLoading.set(true);

    const filters = {
      start_date: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      end_date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    };

    if (!filters.start_date || !filters.end_date) {
      return;
    }

    this._dashboardService.summaryGgrDetailsWeekly(filters)
      .pipe(finalize(() => this.summaryGgrDetailsWeeklyLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.summaryGgrDetailsWeeklyData.set(response.data);

            this.mountSummaryGgrDetailsWeeklyData();
            this.mountSummaryGgrDetailsWeeklyOptions();
          }
        }
      });
  }

  public mountSummaryGgrDetailsWeeklyData(): void {

    this.summaryGgrDetailsWeeklyChartData.set({
      labels: [
        this.summaryGgrDetailsWeeklyData()?.previous_week_2.period,
        this.summaryGgrDetailsWeeklyData()?.previous_week_1.period,
        this.summaryGgrDetailsWeeklyData()?.current_week.period,
        this.summaryGgrDetailsWeeklyData()?.projection_next_week.period
      ],
      datasets: [
        {
          yAxisID: 'y1',
          type: 'line',
          label: '',
          data: [
            this.summaryGgrDetailsWeeklyData()?.previous_week_2.data.conversion_ggr_percentage,
            this.summaryGgrDetailsWeeklyData()?.previous_week_1.data.conversion_ggr_percentage,
            this.summaryGgrDetailsWeeklyData()?.current_week.data.conversion_ggr_percentage,
            this.summaryGgrDetailsWeeklyData()?.projection_next_week.data.conversion_ggr_percentage
          ],
          borderWidth: 1.5,
          borderColor: '#E03326',
          pointRadius: 2,
        },
        {
          yAxisID: 'y',
          type: 'bar',
          label: '',
          data: [
            this.summaryGgrDetailsWeeklyData()?.previous_week_2.data.total_ggr,
            this.summaryGgrDetailsWeeklyData()?.previous_week_1.data.total_ggr,
            this.summaryGgrDetailsWeeklyData()?.current_week.data.total_ggr,
            this.summaryGgrDetailsWeeklyData()?.projection_next_week.data.total_ggr
          ],
          borderWidth: 1,
          barPercentage: 0.6,
          categoryPercentage: 0.7,
          borderRadius: 6,
          backgroundColor: [
            '#4FCF7599',
            '#4FCF7599',
            '#4FCF7599',
            '#4FCF7533',
          ],
          borderColor: [
            '#4FCF75FF',
            '#4FCF75FF',
            '#4FCF75FF',
            '#4FCF7599',
          ]
        },
      ]
    });

    this.summaryGgrDetailsWeeklyChart?.refresh();
  }

  public mountSummaryGgrDetailsWeeklyOptions(): void {
    this.summaryGgrDetailsWeeklyOptions.set({
      ...barChartDefaultOptions,
      maintainAspectRatio: false,
      plugins: {
        ...barChartDefaultOptions?.plugins,
        legend: {
          display: false
        },
        tooltip: {
          ...barChartDefaultOptions?.plugins?.tooltip,
          callbacks: {
            label: function(context) {
              if (context.dataset.type === 'bar') {
                return [
                  "Total GGR",
                  formatCurrency(Number(context.raw), 'pt_BR', 'R$')
                ];
              }

              return [
                "Conversão GGR",
                "GGR/Turnover",
                context.raw + '%'
              ];
            }
          }
        }
      },
      scales: {
        ...barChartDefaultOptions?.scales,
        y: {
          grid: {
            display: true,
          },
          ticks: {
            callback: function (value, index, ticks) {
              if (+value >= 1000000) {
                return `${(+value / 1000000)}M`;
              }

              if (+value >= 1000) {
                return `${(+value / 1000)}K`;
              }

              return `${value}`
            }
          },
          beginAtZero: true
        },
        y1: {
          type: 'linear',
          position: 'right',
          min: 0,
          ticks: {
            callback: function (value, index, ticks) {
              return value + '%';
            }
          },
          grid: {
            display: false
          }
        }
      },
    });
  }

  public getSummaryGgrDetailsMonthly(): void {
    this.summaryGgrDetailsMonthlyLoading.set(true);

    const filters = {
      start_date: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      end_date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    };

    if (!filters.start_date || !filters.end_date) {
      return;
    }

    this._dashboardService.summaryGgrDetailsMonthly(filters)
      .pipe(finalize(() => this.summaryGgrDetailsMonthlyLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.summaryGgrDetailsMonthlyData.set(response.data);

            this.mountSummaryGgrDetailsMonthlyData();
            this.mountSummaryGgrDetailsMonthlyOptions();
          }
        }
      });
  }

  public mountSummaryGgrDetailsMonthlyData(): void {

    this.summaryGgrDetailsMonthlyChartData.set({
      labels: [
        this.summaryGgrDetailsMonthlyData()?.previous_month_2.period,
        this.summaryGgrDetailsMonthlyData()?.previous_month_1.period,
        this.summaryGgrDetailsMonthlyData()?.current_month.period,
        this.summaryGgrDetailsMonthlyData()?.projection_next_month.period,
      ],
      datasets: [
        {
          yAxisID: 'y1',
          type: 'line',
          label: '',
          data: [
            this.summaryGgrDetailsMonthlyData()?.previous_month_2.data.conversion_ggr_percentage,
            this.summaryGgrDetailsMonthlyData()?.previous_month_1.data.conversion_ggr_percentage,
            this.summaryGgrDetailsMonthlyData()?.current_month.data.conversion_ggr_percentage,
            this.summaryGgrDetailsMonthlyData()?.projection_next_month.data.conversion_ggr_percentage
          ],
          borderWidth: 1.5,
          borderColor: '#E03326',
          pointRadius: 2,
        },
        {
          yAxisID: 'y',
          type: 'bar',
          label: '',
          data: [
            this.summaryGgrDetailsMonthlyData()?.previous_month_2.data.total_ggr,
            this.summaryGgrDetailsMonthlyData()?.previous_month_1.data.total_ggr,
            this.summaryGgrDetailsMonthlyData()?.current_month.data.total_ggr,
            this.summaryGgrDetailsMonthlyData()?.projection_next_month.data.total_ggr
          ],
          borderWidth: 1,
          barPercentage: 0.6,
          categoryPercentage: 0.7,
          borderRadius: 6,
          backgroundColor: [
            '#6DA4FF99',
            '#6DA4FF99',
            '#6DA4FF99',
            '#6DA4FF33',
          ],
          borderColor: [
            '#6DA4FFFF',
            '#6DA4FFFF',
            '#6DA4FFFF',
            '#6DA4FF99',
          ]
        },
      ]
    });

    this.summaryGgrDetailsMonthlyChart?.refresh();
  }

  public mountSummaryGgrDetailsMonthlyOptions(): void {
    this.summaryGgrDetailsMonthlyOptions.set({
      ...barChartDefaultOptions,
      maintainAspectRatio: false,
      plugins: {
        ...barChartDefaultOptions?.plugins,
        legend: {
          display: false
        },
        tooltip: {
          ...barChartDefaultOptions?.plugins?.tooltip,
          callbacks: {
            label: function(context) {
              if (context.dataset.type === 'bar') {
                return [
                  "Total GGR",
                  formatCurrency(Number(context.raw), 'pt_BR', 'R$')
                ];
              }

              return [
                "Conversão GGR",
                "GGR/Turnover",
                context.raw + '%'
              ];
            }
          }
        }
      },
      scales: {
        ...barChartDefaultOptions?.scales,
        y: {
          grid: {
            display: true,
          },
          ticks: {
            callback: function (value, index, ticks) {
              if (+value >= 1000000) {
                return `${(+value / 1000000)}M`;
              }

              if (+value >= 1000) {
                return `${(+value / 1000)}K`;
              }

              return `${value}`
            }
          },
          beginAtZero: true
        },
        y1: {
          type: 'linear',
          position: 'right',
          min: 0,
          ticks: {
            callback: function (value, index, ticks) {
              return value + '%';
            }
          },
          grid: {
            display: false
          }
        }
      },
    });
  }

  public getSummaryTurnover(): void {
    this.summaryTurnoverLoading.set(true);

    const filters = {
      date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    }

    this._dashboardService.summaryGgrDetailsTurnover(filters)
      .pipe(finalize(() => this.summaryTurnoverLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.summaryTurnoverData.set(response.data);

            this.mountSummaryTurnoverData();
            this.mountSummaryTurnoverOptions();
          }
        }
      });
  }

  public mountSummaryTurnoverData(): void {
    this.summaryTurnoverChartData.set({
      labels: this.summaryTurnoverData()?.current_date.total.hourly_data.map((item: SummaryGgrDetailsTurnoverItemDataHourlyData) => item.hour),
      datasets: [
        {
          label: 'Hoje',
          data: this.summaryTurnoverData()?.current_date.total.hourly_data.map((item: SummaryGgrDetailsTurnoverItemDataHourlyData) => item.total_bet_value),
          borderWidth: 2,
          backgroundColor: '#4FCF75',
          borderColor: '#4FCF75',
          pointRadius: 2,
        },
        {
          label: 'D-7',
          data: this.summaryTurnoverData()?.date_minus_7.total.hourly_data.map((item: SummaryGgrDetailsTurnoverItemDataHourlyData) => item.total_bet_value),
          borderWidth: 2,
          backgroundColor: '#E95F60',
          borderColor: '#E95F60',
          pointRadius: 2,
        },
        {
          label: 'D-14',
          data: this.summaryTurnoverData()?.date_minus_14.total.hourly_data.map((item: SummaryGgrDetailsTurnoverItemDataHourlyData) => item.total_bet_value),
          borderWidth: 2,
          backgroundColor: '#6DA4FF',
          borderColor: '#6DA4FF',
          pointRadius: 2,
        },
      ],
      fill: false,
      tension: 0,
      pointRadius: 1,
    });
  }

  public mountSummaryTurnoverOptions(): void {

    this.summaryTurnoverOptions.set({
      ...lineChartDefaultOptions,
      maintainAspectRatio: false,
      scales: {
        ...lineChartDefaultOptions?.scales,
        y: {
          grid: {
            display: true,
          },
          ticks: {
            callback: function (value, index, ticks) {
              if (+value > 1000) {
                return `R$ ${(+value / 1000)}K`;
              }

              return `R$ ${value}`
            }
          },
          beginAtZero: true
        }
      },
      plugins: {
        ...lineChartDefaultOptions?.plugins,
        tooltip: {
          ...lineChartDefaultOptions?.plugins?.tooltip,
          callbacks: {
            label: (context: any) => {
              return formatCurrency(context.raw, 'pt_BR', 'R$');
            }
          }
        }
      }
    });
  }

  public getSummaryGgrDetailsOperations(): void {
    this.summaryGgrDetailsOperationsLoading.set(true);

    const filters = {
      start_date: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      end_date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    };

    if (!filters.start_date || !filters.end_date) {
      return;
    }

    this._dashboardService.summaryGgrDetailsOperations(filters)
      .pipe(finalize(() => this.summaryGgrDetailsOperationsLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.summaryGgrDetailsOperationsData.set(response.data);
          }
        }
      });
  }

  public getSummaryLifeCycle(): void {
    this.summaryLifeCycleLoading.set(true);

    const filters = {
      start_date: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      end_date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    };

    if (!filters.start_date || !filters.end_date) {
      return;
    }

    this._dashboardService.summaryFinancialLifeCycle(filters)
      .pipe(finalize(() => this.summaryLifeCycleLoading.set(false)))
      .subscribe({
        next: (response) => {
          console.log(response.data);
          if (response.success) {
            this.summaryLifeCycleData.set(response.data);
          }
        }
      });
  }

  public getSummaryChurn(): void {
    this.summaryChurnLoading.set(true);

    const filters = {
      churn_type: this.summaryChurnShowType,
      reference_month_start: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      reference_month_end: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    }

    this._dashboardService.summaryFinancialChurn(filters)
      .pipe(finalize(() => this.summaryChurnLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.summaryChurnData.set(response.data);
          }
        }
      });
  }

  public getSummaryChurnDetailed(): void {
    this.summaryChurnDetailedLoading.set(true);

    this.summaryChurnDetailedData.set(null);

    const filters = {
      ...this.summaryChurnPageControl,
      churn_type: this.summaryChurnShowType,
      utm_source_filter: this.summaryChurnDetailedUtm,
      reference_month_start: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      reference_month_end: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    }

    this._dashboardService.summaryFinancialChurnDetailed(filters)
      .pipe(finalize(() => this.summaryChurnDetailedLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.summaryChurnDetailedData.set(response.data);

          this.summaryChurnPageControl.page = response.meta.current_page;
          this.summaryChurnPageControl.itemCount = response.meta.total;
        }
      });
  }

  public onPageChange(e: PaginatorState): void {
    this.summaryChurnPageControl.page = (e?.page || 0) + 1;
    this.summaryChurnPageControl.take = (e?.rows || 10);

    this.getSummaryChurnDetailed();
  }

  public openChurnDialog(): void {
    this.churnDialogVisible = true;
    this.getSummaryChurnDetailed();
  }

  public getSummaryLifeCycleDataByEnum(key: FinancialLifeCycleItemCategory): FinancialLifeCycleItem|null {
    return this.summaryLifeCycleData()?.lifecycle.find((item: FinancialLifeCycleItem) => item.categoria === key) ?? null;
  }

  public topCasinoGamesTop5(): void {
    this.topCasinoGamesTop5Loading.set(true);

    const filters = {
      start_date: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      end_date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    };

    if (!filters.start_date || !filters.end_date) {
      return;
    }

    this._dashboardService.topCasinoGamesTop5(filters)
      .pipe(finalize(() => this.topCasinoGamesTop5Loading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.topCasinoGamesTop5Data.set(response.data);
          }
        }
      })
  }

  public topSportsMarketsTop5(): void {
    this.topSportsMarketsTop5Loading.set(true);

    const filters = {
      start_date: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      end_date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    };

    if (!filters.start_date || !filters.end_date) {
      return;
    }

    this._dashboardService.topSportsMarketsTop5(filters)
      .pipe(finalize(() => this.topSportsMarketsTop5Loading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.topSportsMarketsTop5Data.set(response.data);
          }
        }
      })
  }
}
